import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * React Error Boundary
 * 捕获组件树中的 JavaScript 错误，记录错误并显示降级 UI
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // 更新 state 使下一次渲染能够显示降级后的 UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 记录错误到日志系统
    console.error('[ErrorBoundary] 捕获到错误:', {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
        toString: error.toString(),
      },
      errorInfo: {
        componentStack: errorInfo.componentStack,
      },
    });

    // 記錄錯誤到後端 app_error_logs 表和 Sentry
    Promise.all([
      import('../utils/errorLogger').then(({ logErrorToBackend }) => {
        logErrorToBackend(error, {
          fields: {
            event: 'component_error',
            errorCode: 'REACT_ERROR_BOUNDARY',
            component: 'ErrorBoundary',
          },
          componentStack: errorInfo.componentStack || undefined,
          context: {
            page: window.location.pathname,
            errorBoundary: true,
          },
        });
      }),
      import('../utils/sentry').then(({ captureException }) => {
        captureException(error, {
          level: 'error',
          tags: {
            event: 'component_error',
            errorCode: 'REACT_ERROR_BOUNDARY',
            component: 'ErrorBoundary',
          },
          extra: {
            componentStack: errorInfo.componentStack,
            page: window.location.pathname,
          },
        });
      }),
    ]).catch(() => {
      // 如果導入失敗，忽略錯誤（避免無限迴圈）
    });

    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      // 如果有自定义 fallback，使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 默认错误 UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
            <div className="text-6xl text-center mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-red-600 mb-4 text-center">
              應用程式發生錯誤
            </h2>

            {this.state.error && (
              <div className="mb-4">
                <p className="text-gray-700 font-semibold mb-2">錯誤訊息:</p>
                <p className="text-red-600 bg-red-50 p-3 rounded-lg">
                  {this.state.error.message || this.state.error.toString()}
                </p>
              </div>
            )}

            {this.state.errorInfo?.componentStack && (
              <details className="mb-4">
                <summary className="text-gray-700 font-semibold cursor-pointer mb-2">
                  詳細錯誤資訊（點擊展開）
                </summary>
                <pre className="bg-gray-100 p-4 rounded-lg overflow-auto text-xs">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className="text-center">
              <button
                onClick={() => {
                  this.setState({
                    hasError: false,
                    error: null,
                    errorInfo: null,
                  });
                  window.location.reload();
                }}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                重新載入頁面
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
